/**
 * シーンの尺配分（backend `ProportionalTimingProvider::distribute()` の1:1移植）。
 *
 * B5のffmpegレンダラーは、timeline編集後（timeline.captionsが非空）は歌唱アライメントではなく
 * 「歌詞行数の比で楽曲尺を配分し、シーンに最小尺(2.5秒)を保証する」という配分を使う
 * （SongAdRenderPlanner::windowsFor → ProportionalTimingProvider::distribute）。
 * プレビュー/詳細編集のシーン枠がこのロジックとズレると、書き出したMP4と見た目が合わなくなるため、
 * 同じアルゴリズムをここに移植して単一の正とする（backend側を変更したら必ずここも追従させる）。
 */

/** シーンの最小尺。backendの MIN_SCENE_SECONDS と同値 */
export const MIN_SCENE_SECONDS = 2.5;

export interface SceneWindow {
  start: number;
  end: number;
}

function distributeSceneDurations(lineCounts: number[], totalSeconds: number): number[] {
  const counts = lineCounts.map((c) => Math.max(1, c));
  const total = counts.reduce((a, b) => a + b, 0) || 1;
  const raw = counts.map((c) => totalSeconds * (c / total));

  const minTotal = MIN_SCENE_SECONDS * counts.length;
  if (minTotal >= totalSeconds) {
    // 曲が短すぎて全シーンに最小尺を配れない場合は等分する
    return counts.map(() => totalSeconds / counts.length);
  }

  let deficit = 0;
  let surplusTotal = 0;
  raw.forEach((d) => {
    if (d < MIN_SCENE_SECONDS) {
      deficit += MIN_SCENE_SECONDS - d;
    } else {
      surplusTotal += d - MIN_SCENE_SECONDS;
    }
  });

  if (deficit <= 0 || surplusTotal <= 0) {
    return raw;
  }

  return raw.map((d) => {
    if (d < MIN_SCENE_SECONDS) return MIN_SCENE_SECONDS;
    const surplus = d - MIN_SCENE_SECONDS;
    return d - deficit * (surplus / surplusTotal);
  });
}

/**
 * 並び替え済み（skip除外済み）のシーン列に楽曲尺を配分する。
 * `lineCounts[i]` はシーンiの歌詞行数（0でも1行分として扱われる。backendのmax(1, ...)と同じ）。
 * 最終シーンの end は totalSeconds ちょうどに寄せる（端数を吸収）。
 */
export function computeSceneWindows(lineCounts: number[], totalSeconds: number): SceneWindow[] {
  if (lineCounts.length === 0 || totalSeconds <= 0) return [];

  const durations = distributeSceneDurations(lineCounts, totalSeconds);
  const windows: SceneWindow[] = [];
  let cursor = 0;

  lineCounts.forEach((_, i) => {
    const start = cursor;
    const end = i === lineCounts.length - 1 ? totalSeconds : cursor + durations[i];
    cursor = end;
    windows.push({ start: round3(start), end: round3(end) });
  });

  return windows;
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

/**
 * CTAカード（v4 D/B5/F9）の尺確保ロジック（backend `SongAdRenderPlanner::applyCta()` の1:1移植）。
 *
 * 総尺は変えず、末尾のシーンから順に尺を借りる（各シーンに `MIN_SCENE_AFTER_TRIM` 秒は残す）。
 * 借りられた尺の合計が `CTA_MIN_SECONDS` を下回るなら「入れる余地なし」として null を返す
 * （呼び出し側はCTAカードを出さない。backendの `not enough room` ログと同じ扱い）。
 */
export const CTA_DEFAULT_SECONDS = 2.5;
export const CTA_MIN_SECONDS = 1.2;
const MIN_SCENE_AFTER_TRIM = 1.0;

export interface CtaWindowResult {
  /** CTA分だけ末尾を削ったシーン枠（inputの配列と同じ順序・同じ長さ） */
  windows: SceneWindow[];
  /** CTAカードの開始秒（終了は呼び出し側が持つ totalSeconds） */
  ctaStart: number;
}

export function computeCtaWindow(windows: SceneWindow[], totalSeconds: number): CtaWindowResult | null {
  if (windows.length === 0 || totalSeconds <= 0) return null;

  let remaining = CTA_DEFAULT_SECONDS;
  const trims = windows.map(() => 0);

  for (let i = windows.length - 1; i >= 0 && remaining > 0.001; i--) {
    const duration = windows[i].end - windows[i].start;
    const take = Math.min(remaining, Math.max(0, duration - MIN_SCENE_AFTER_TRIM));
    trims[i] = take;
    remaining -= take;
  }

  const ctaDuration = CTA_DEFAULT_SECONDS - remaining;
  if (ctaDuration < CTA_MIN_SECONDS) return null;

  let cursor = 0;
  const trimmed = windows.map((w, i) => {
    const duration = w.end - w.start - trims[i];
    const start = round3(cursor);
    const end = round3(cursor + duration);
    cursor = end;
    return { start, end };
  });

  return { windows: trimmed, ctaStart: round3(cursor) };
}
